import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isNil } from 'lodash-es';
import { keepZeroOrLine } from '@/utils/utils';
import { ShareDataWrapperContext } from '../../../../index';
import IconAreaPng from '../../../../imgs/area.png';
import IconAreaInactivePng from '../../../../imgs/icon-area-inactive.png';
import {
  apiV2ElectricMapDataAreaPost,
  V2ElectricMapDataAreaPostResponse,
  V2ElectricMapDataAreaV2PostResponse,
} from '@maxtropy/device-customer-apis-v2';
import type {
  RenderStreamingVideoElement,
  UnityCall,
  UnityCameraData,
  UnityClickDetail,
} from '../../../UnityRenderMap';
import {
  clearUnityBusinessCleanupPayloads,
  setUnityBusinessCleanupPayloads,
} from '../../../UnityRenderMap/businessCleanup';
import { getUnityPosition, notEmpty } from '../../../UnityRenderMap/utils';
import { UnityOverlay, useSharedUnityMap } from '../../../SharedUnityMapLayer';
import mapViewStyles from '../MapView/index.module.scss';

interface MapViewProps {
  className?: string;
  children?: React.ReactNode;
  initialCameraData?: UnityCameraData[] | null;
  onUnityClick?: (detail: UnityClickDetail) => void;
  onReady?: (helpers: { callUnity: UnityCall; videoElement: RenderStreamingVideoElement }) => void | Promise<void>;
}

export type AreaItem = Exclude<V2ElectricMapDataAreaPostResponse['list'], undefined>[number];
export type StationItem = Exclude<V2ElectricMapDataAreaV2PostResponse['list'], undefined>[number];

const AREA_MARKER_PARENT = 'Energy';
const AREA_LABEL_PARENT = 'CustomLabel';
const AREA_LABEL_PREFAB = 'Prefabs/purpleLabelRoot';

const getAreaInstanceName = (area: AreaItem) => area.name ?? `area-${area.id ?? 'unknown'}`;

const estimateTextWidthPercent = (text: string) =>
  Array.from(text).reduce((sum, char) => sum + (/[\u4e00-\u9fa5]/.test(char) ? 5.2 : 4.2), 0);

const getCenteredTextPos = (centerPercent: number, text: string) =>
  Math.max(0, centerPercent - estimateTextWidthPercent(text) / 2).toFixed(1);

const createAreaLabelRichText = (powerText: string, electricityText: string) => {
  const powerTitle = '实时功率';
  const electricityTitle = '本月用电';
  const leftCenter = 25;
  const rightCenter = 75;
  const separator = '<color=#FFFFFF33><pos=50%>|</color>';

  return [
    `<color=#52E7FF><pos=${getCenteredTextPos(
      leftCenter,
      powerTitle
    )}%>${powerTitle}</color>${separator}<color=#52E7FF><pos=${getCenteredTextPos(
      rightCenter,
      electricityTitle
    )}%>${electricityTitle}</color>`,
    `<color=#FFFFFF><pos=${getCenteredTextPos(
      leftCenter,
      powerText
    )}%>${powerText}</color>${separator}<color=#FFFFFF><pos=${getCenteredTextPos(
      rightCenter,
      electricityText
    )}%>${electricityText}</color>`,
  ].join('\n');
};

const createAreaLabelPayload = (
  areaList: AreaItem[],
  videoElement?: RenderStreamingVideoElement,
  visible = true,
  includeRemove = true
) => {
  const instances = visible
    ? areaList
        .map(area => {
          const position = getUnityPosition(area, videoElement, '[UnityPowerMap]');
          if (!position) {
            console.warn('[UnityPowerMap] skip area label without unity position', {
              id: area.id,
              name: area.name,
              pointLng: area.pointLng,
              pointLat: area.pointLat,
            });
            return null;
          }

          const powerText = `${keepZeroOrLine(area.power)}kW`;
          const electricityText = `${keepZeroOrLine(area.electricity)}kWh`;

          return {
            name: getAreaInstanceName(area),
            position,
            rotation: [0, 0, 0] as [number, number, number],
            grounded: false,
            parent: AREA_MARKER_PARENT,
            properties: {
              id: area.id,
              code: area.code,
              name: area.name,
              energyUnitId: area.energyUnitId,
              pointLng: area.pointLng,
              pointLat: area.pointLat,
              power: area.power,
              electricity: area.electricity,
              labelType: 'powerArea',
              key1: createAreaLabelRichText(powerText, electricityText),
              powerText,
              electricityText,
            },
          };
        })
        .filter(notEmpty)
    : [];

  return {
    type: 'refreshModel' as const,
    data: {
      remove: includeRemove
        ? [
            {
              path: 'Prefabs/Empty',
              type: 'prefab' as const,
              instances: areaList.map(area => ({
                name: getAreaInstanceName(area),
                parent: AREA_MARKER_PARENT,
              })),
            },
            {
              path: AREA_LABEL_PREFAB,
              type: 'prefab' as const,
              instances: areaList.map(area => ({
                name: getAreaInstanceName(area),
                parent: AREA_LABEL_PARENT,
              })),
            },
          ]
        : [],
      add:
        visible && instances.length
          ? [
              {
                path: 'Prefabs/Empty',
                type: 'prefab' as const,
                instances,
                label: {
                  path: AREA_LABEL_PREFAB,
                  height: 70,
                  parent: AREA_LABEL_PARENT,
                },
              },
            ]
          : [],
    },
  };
};

const UnityMap: FC<MapViewProps> = props => {
  const { onReady, onUnityClick, children, initialCameraData: _initialCameraData, ...restProps } = props;
  const { isStandardSize } = useContext(ShareDataWrapperContext);
  const { unityReady: sharedUnityReady, callUnity, videoElement, registerUnityClickHandler } = useSharedUnityMap();
  const unityReadyRef = useRef<{ callUnity: UnityCall; videoElement: RenderStreamingVideoElement } | null>(null);
  const lastSyncSignatureRef = useRef<string>('');
  const areaLabelsMountedRef = useRef(false);
  const [originList, setOriginList] = useState<AreaItem[]>([]);
  const [unityReady, setUnityReady] = useState(false);
  const [markerVisible, setMarkerVisible] = useState({
    area: true,
  });

  useEffect(() => registerUnityClickHandler(onUnityClick), [onUnityClick, registerUnityClickHandler]);

  useEffect(() => {
    let canceled = false;

    apiV2ElectricMapDataAreaPost({}).then(res => {
      if (canceled) return;
      setOriginList(res.list ?? []);
    });

    return () => {
      canceled = true;
    };
  }, []);

  const areaList = useMemo(
    () => originList.filter(area => !area.ggdFlag && isNil(area.roomId) && !isNil(area.energyUnitId)),
    [originList]
  );

  const syncUnityAreaLabels = useCallback(
    async ({ callUnity, videoElement }: { callUnity: UnityCall; videoElement: RenderStreamingVideoElement }) => {
      if (!areaList.length) {
        console.info('[UnityPowerMap] no area labels to sync');
        return;
      }

      const includeRemove = !markerVisible.area || areaLabelsMountedRef.current;
      const payload = createAreaLabelPayload(areaList, videoElement, markerVisible.area, includeRemove);
      const addCount = payload.data.add[0]?.instances.length ?? 0;
      const syncSignature = JSON.stringify(payload.data);
      if (lastSyncSignatureRef.current === syncSignature) {
        console.info('[UnityPowerMap] area labels unchanged, skip sync', {
          areaCount: areaList.length,
          addCount,
          visible: markerVisible.area,
        });
        return;
      }

      console.info('[UnityPowerMap] syncing area labels', {
        areaCount: areaList.length,
        addCount,
        visible: markerVisible.area,
        includeRemove,
        payload,
      });

      await callUnity(payload);
      lastSyncSignatureRef.current = syncSignature;
      areaLabelsMountedRef.current = markerVisible.area && addCount > 0;
      if (areaLabelsMountedRef.current) {
        setUnityBusinessCleanupPayloads('powerArea', [createAreaLabelPayload(areaList, undefined, false, true)]);
      } else {
        clearUnityBusinessCleanupPayloads('powerArea');
      }
      console.info('[UnityPowerMap] area labels synced', { addCount });
    },
    [areaList, markerVisible.area]
  );

  useEffect(() => {
    const helpers = unityReadyRef.current;
    if (!unityReady || !helpers) return;

    syncUnityAreaLabels(helpers).catch(error => {
      console.error('[UnityPowerMap] area label sync failed', error);
    });
  }, [syncUnityAreaLabels, unityReady]);

  const handleUnityReady = useCallback(
    (helpers: { callUnity: UnityCall; videoElement: RenderStreamingVideoElement }) => {
      unityReadyRef.current = helpers;
      setUnityReady(true);
      onReady?.(helpers);
    },
    [onReady]
  );

  useEffect(() => {
    if (!sharedUnityReady || !callUnity || !videoElement) return;

    handleUnityReady({ callUnity, videoElement });
  }, [callUnity, handleUnityReady, sharedUnityReady, videoElement]);

  return (
    <UnityOverlay {...restProps}>
      <div className={mapViewStyles.legendList} style={{ left: isStandardSize ? '20px' : '484px' }}>
        <div
          className={`${markerVisible.area ? mapViewStyles.activeAreaItem : mapViewStyles.inActiveItem}`}
          style={{ pointerEvents: 'all' }}
          onClick={() => setMarkerVisible(prev => ({ ...prev, area: !prev.area }))}
        >
          <img src={markerVisible.area ? IconAreaPng : IconAreaInactivePng} alt="" />
          <div>区域({areaList.length})</div>
        </div>
      </div>
      {children}
    </UnityOverlay>
  );
};

export default UnityMap;
